import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from 'recharts';
import { teacherDeploymentAPI } from '../../services/teacherDeploymentApi';

const StatCard: React.FC<{ label: string; value: string | number; subtitle?: string; color?: string }> = ({
  label, value, subtitle, color = '#1976d2',
}) => (
  <Paper sx={{ p: 2.5, textAlign: 'center', borderTop: `4px solid ${color}` }}>
    <Typography variant="h3" fontWeight={700} color={color}>{value}</Typography>
    <Typography variant="body2" fontWeight={600}>{label}</Typography>
    {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
  </Paper>
);

const TeacherDeploymentDashboard: React.FC = () => {
  const [tab, setTab] = React.useState(0);

  const { data: overview, isLoading: loadingOverview } = useQuery({
    queryKey: ['teacher-deployment-overview'],
    queryFn: () => teacherDeploymentAPI.getOverview(),
    select: (res) => res.data,
  });

  const { data: gaps, isLoading: loadingGaps } = useQuery({
    queryKey: ['teacher-deployment-gaps'],
    queryFn: () => teacherDeploymentAPI.getGapAnalysis(),
    select: (res) => res.data,
  });

  const { data: qualifications, isLoading: loadingQuals } = useQuery({
    queryKey: ['teacher-deployment-qualifications'],
    queryFn: () => teacherDeploymentAPI.getQualifications(),
    select: (res) => res.data,
  });

  if (loadingOverview || loadingGaps || loadingQuals) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  const ptrChartData = overview?.districts?.map((d: any) => ({
    district: d.district,
    ptr: d.overall_ptr,
    zone: d.zone,
  })) || [];

  return (
    <Box p={3}>
      <Typography variant="h4" fontWeight={700} mb={0.5}>
        PTR & Teacher Deployment Analytics
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Pupil-Teacher Ratio analysis, deployment gaps, and qualification distribution
        {overview?.academic_year && ` (${overview.academic_year})`}
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={2} mb={4}>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="National PTR"
            value={`${overview?.national?.ptr || 0}:1`}
            subtitle={`Target: ${overview?.target_ptr || 40}:1`}
            color={overview?.national?.exceeds_threshold ? '#f44336' : '#4caf50'}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Districts Above Threshold"
            value={`${overview?.districts_exceeding_threshold || 0}/${overview?.total_districts || 0}`}
            subtitle={`Threshold: ${overview?.target_ptr || 40}:1`}
            color={overview?.districts_exceeding_threshold > 0 ? '#f44336' : '#4caf50'}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Total Teacher Gap"
            value={gaps?.total_teachers_needed || 0}
            subtitle="Additional teachers needed"
            color="#ff9800"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Worst District PTR"
            value={overview?.worst_district ? `${overview.worst_district.overall_ptr}:1` : 'N/A'}
            subtitle={overview?.worst_district?.district || ''}
            color="#f44336"
          />
        </Grid>
      </Grid>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="PTR by District" />
        <Tab label="Gap Analysis" />
        <Tab label="Qualifications" />
      </Tabs>

      {/* Tab 0: PTR Chart */}
      {tab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>PTR by District</Typography>
          <ResponsiveContainer width="100%" height={450}>
            <BarChart data={ptrChartData} layout="vertical" margin={{ left: 120 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="district" width={110} tick={{ fontSize: 12 }} />
              <Tooltip formatter={(value: number) => `${value}:1`} />
              <ReferenceLine x={overview?.target_ptr || 40} stroke="#f44336" strokeDasharray="5 5" label={{ value: `Target ${overview?.target_ptr || 40}:1`, position: 'top' }} />
              <Bar dataKey="ptr" name="PTR" radius={[0, 4, 4, 0]}>
                {ptrChartData.map((entry: any, index: number) => (
                  <Cell key={index} fill={entry.ptr > (overview?.target_ptr || 40) ? '#f44336' : entry.zone === 'Pemba' ? '#ff9800' : '#4caf50'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Tab 1: Gap Analysis Table */}
      {tab === 1 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>Teacher Deployment Gap Analysis</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>District</strong></TableCell>
                  <TableCell><strong>Zone</strong></TableCell>
                  <TableCell align="right"><strong>Students</strong></TableCell>
                  <TableCell align="right"><strong>Teachers</strong></TableCell>
                  <TableCell align="right"><strong>Current PTR</strong></TableCell>
                  <TableCell align="right"><strong>Teachers Needed</strong></TableCell>
                  <TableCell align="right"><strong>Gap %</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gaps?.districts?.map((row: any) => (
                  <TableRow key={row.district} sx={{ bgcolor: row.teachers_needed > 0 ? '#fff3e0' : undefined }}>
                    <TableCell>{row.district}</TableCell>
                    <TableCell>
                      <Chip label={row.zone} size="small" color={row.zone === 'Pemba' ? 'warning' : 'info'} variant="outlined" />
                    </TableCell>
                    <TableCell align="right">{row.students.toLocaleString()}</TableCell>
                    <TableCell align="right">{row.current_teachers.toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${row.current_ptr}:1`}
                        size="small"
                        sx={{ bgcolor: row.current_ptr > 40 ? '#f44336' : '#4caf50', color: 'white', fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ color: row.teachers_needed > 0 ? '#f44336' : '#4caf50', fontWeight: 600 }}>
                      {row.teachers_needed > 0 ? `+${row.teachers_needed}` : '0'}
                    </TableCell>
                    <TableCell align="right">{row.gap_percentage}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Tab 2: Qualifications */}
      {tab === 2 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>Teacher Qualification Distribution</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>District</strong></TableCell>
                  <TableCell align="right"><strong>Total Teachers</strong></TableCell>
                  <TableCell align="right"><strong>Qualified (Diploma+)</strong></TableCell>
                  <TableCell align="right"><strong>Qualified %</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {qualifications?.districts?.map((row: any) => (
                  <TableRow key={row.district}>
                    <TableCell>{row.district}</TableCell>
                    <TableCell align="right">{row.total.toLocaleString()}</TableCell>
                    <TableCell align="right">{row.qualified_count.toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${row.qualified_pct}%`}
                        size="small"
                        sx={{ bgcolor: row.qualified_pct >= 80 ? '#4caf50' : row.qualified_pct >= 60 ? '#ff9800' : '#f44336', color: 'white', fontWeight: 600 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default TeacherDeploymentDashboard;
